import get from 'lodash/get';
import { startCase } from 'lodash';
import store from '../store/markettingContentGeneration';
import { editedContentReferenceSegment } from '../store/markettingContentGeneration/stateSegments/editedContentReferenceSegment';
import { DUMMY_OPTION_ID } from '../constants/common';

/**
 * Processes contentRefData from API responses and stores edited content references
 * in the editedContentReferenceSegment for persistent state tracking.
 *
 * @param rawApiResponse - The raw API response containing generatedContent with contentRefData
 * @param contentId - The content ID to process contentRefData for
 */

export const processContentRefData = (
  rawApiResponse: any,
  contentId: string
) => {
  const generatedContent = get(rawApiResponse, 'data.generatedContent', []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  generatedContent.forEach((content: any) => {
    if (content.contentRefData && content.id === contentId) {
      const { contentRefData } = content;

      // contentRefData is now an array of objects
      if (Array.isArray(contentRefData)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        contentRefData.forEach((refDataItem: any) => {
          // Process different content types based on structure

          if (refDataItem.optionId) {
            const optionId = refDataItem.optionId;

            // Core messages and email modules format
            if (refDataItem.referenceData?.editedAttributes) {
              store.set(editedContentReferenceSegment, prev => ({
                ...prev,
                [contentId]: {
                  ...prev[contentId],
                  [optionId]: refDataItem.referenceData.editedAttributes,
                },
              }));
            }

            // Frame-based content format
            if (refDataItem.frames && Array.isArray(refDataItem.frames)) {
              const editedFrameIds: string[] = [];
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              refDataItem.frames.forEach((frame: any) => {
                if (frame.id && frame.referenceData?.editedAttributes) {
                  editedFrameIds.push(frame.id);
                }
              });

              if (editedFrameIds.length > 0) {
                store.set(editedContentReferenceSegment, prev => ({
                  ...prev,
                  [contentId]: {
                    ...prev[contentId],
                    [optionId]: editedFrameIds,
                  },
                }));
              }
            }

            // Social media format with shortForm/longForm
            if (refDataItem.shortForm || refDataItem.longForm) {
              const editedAttributes: string[] = [];

              if (refDataItem.shortForm?.referenceData?.editedAttributes) {
                editedAttributes.push(
                  ...refDataItem.shortForm.referenceData.editedAttributes.map(
                    (attr: string) => `shortForm${startCase(attr)}`
                  )
                );
              }

              if (refDataItem.longForm?.referenceData?.editedAttributes) {
                editedAttributes.push(
                  ...refDataItem.longForm.referenceData.editedAttributes.map(
                    (attr: string) => `longForm${startCase(attr)}`
                  )
                );
              }

              if (editedAttributes.length > 0) {
                store.set(editedContentReferenceSegment, prev => ({
                  ...prev,
                  [contentId]: {
                    ...prev[contentId],
                    [optionId]: editedAttributes,
                  },
                }));
              }
            }

            // Call to action format
            if (refDataItem.callToAction?.referenceData?.editedAttributes) {
              store.set(editedContentReferenceSegment, prev => ({
                ...prev,
                [contentId]: {
                  ...prev[contentId],
                  [optionId]: [
                    ...(prev[contentId]?.[optionId] || []),
                    ...refDataItem.callToAction.referenceData.editedAttributes,
                  ],
                },
              }));
            }
          }
          // Document summary format OR core message & DSP upload flows without optionId
          else if (refDataItem.referenceData?.editedAttributes) {
            // Use DUMMY_OPTION_ID for core message & DSP upload flows where optionId is not provided
            store.set(editedContentReferenceSegment, prev => ({
              ...prev,
              [contentId]: {
                ...prev[contentId],
                [DUMMY_OPTION_ID]: [
                  ...(prev[contentId]?.[DUMMY_OPTION_ID] || []),
                  ...refDataItem.referenceData.editedAttributes,
                ],
              },
            }));
          }
        });
      }
    }
  });
};
